/**
 * Project data and utilities
 */

import { Project } from '@/types/project';

export const projects: Project[] = [
  {
    id: 'lua-resty-jwt',
    title: 'lua-resty-jwt',
    description: 'JWT library for OpenResty/NGINX using native OpenSSL binaries for lightning-fast crypto operations',
    longDescription: `This lovely package I took over in 2018/2019 from SkyLothar that uses your favorite linux OS's packaged openssl binaries to make doing crypto calculations in NGINX super duper fast (lightspeed). The concept here is basically that you have a full async/threaded runtime inside your NGINX gateway that you can be making use of as long as it is non-blocking. Many people are moving away from this concept in favor of edge lambdas in cloud providers like AWS but I would argue that raw boxes are alive and well! I currently have NGINX on all servers I manage and having the capability to do programmatic non-blocking computation inside of a path resolution is superb!`,
    tags: ['Lua', 'OpenResty', 'NGINX', 'JWT', 'Authentication', 'OpenSSL'],
    category: 'open-source',
    githubUrl: 'https://github.com/cdbattags/lua-resty-jwt',
    luaRocksUrl: 'https://luarocks.org/modules/cdbattags/lua-resty-jwt',
    featured: true,
    startDate: '2018-01-01',
    status: 'active',
    highlights: [
      'Used by lua-resty-openidc for OpenID Connect',
      'Lightning-fast crypto using native OpenSSL',
      'Non-blocking async operations in NGINX',
      'Available on LuaRocks package manager',
    ],
    usedBy: [
      {
        name: 'lua-resty-openidc',
        url: 'https://github.com/zmartzone/lua-resty-openidc',
      },
    ],
  },
  {
    id: 'rust-actix-web',
    title: 'Rust + actix-web',
    description: 'Experiments with Rust async/await, std::future, and tokio runtime for high-performance web services',
    longDescription: `There's a fancy new feature coming up in Rust called async/.await that is going to change the game IMO for digital signal processing, web development and any I/O heavy project. The ability to use a pluggable runtime like tokio in the Rust ecosystem means that we're not far off from having a lower level zero cost abstraction for just about every protocol like eBPF. I've been following actix closely as they transition to using the new std::future along with a massive tokio rewrite landing soon!`,
    tags: ['Rust', 'actix-web', 'async/await', 'tokio', 'Performance', 'Web', 'Open Source', 'Systems Programming'],
    category: 'experiment',
    githubUrl: 'https://github.com/cdbattags',
    featured: false,
    startDate: '2019-01-01',
    status: 'archived',
    highlights: [
      'Exploring Rust async/await patterns',
      'Zero-cost abstractions for I/O',
      'Learning tokio runtime',
    ],
  },
  {
    id: 'places-of-interest',
    title: '@dtw/places-of-interest',
    description: 'Collaborative location recommendation app for sharing favorite spots with friends without public exposure',
    longDescription: `Many a friend pass through NYC and it's my duty to make sure I show everyone how much fun this beautiful, bright city is. That said, this city is 100% based on word of mouth. Timeout NYC can only go so far with the recommendations and nobody wants their favorite local spot blowing up! Each and every time a friend passes along a recommendation I store it on my phone and now suddenly I'm approaching 1000 locations in all of the world that I can "recommend". Great and all but I want to take it one step further and beat Google at the "my maps" game by doing group collaborative, low maintenance recommendations in your favorite city! For now I'll have a secret url on that website to get to a "friends-only" spot to add a recommendation. Again, the goal is to let you keep all your spots but let you share them quick and easy! Yelp, Google, Apple, whatever! Copyright on location data is a hard thing and might get a little quirky to figure out but for now it's great to share with my friends that are only in town for the weekend and don't need hand holding through the city!`,
    tags: ['TypeScript', 'React', 'Maps', 'PostgreSQL', 'Geolocation', 'NYC', 'Recommendations', 'DTW'],
    category: 'personal',
    featured: true,
    startDate: '2020-01-01',
    status: 'active',
    highlights: [
      '1000+ curated locations worldwide',
      'Private sharing with friends-only URLs',
      'Better than Google My Maps for collaboration',
      'Import from Yelp, Google, Apple Maps',
    ],
  },
  {
    id: 'grand-central',
    title: '@dtw/grand-central',
    description: 'Multi-framework integration system for running React, Vue, and AngularJS together in a single application',
    longDescription: `Grand Central began as a product requirement for work at Amplify. We have what we call the "Curriculum" application that is about 3-4 years worth of Angular 1.x code that is the main interface for how Amplify users consume our curriculum content in English and science classes. We set off to re-use these Angular components hand in hand with some React code and stumbled into arcadeJHS/AngularVueIntegration which outlined exactly what we wanted to do: 1 DOM instance shell of each framework we want to use together (Vue, React, Angular 1x or Angular 2+). The beauty of this is that we're able to reuse initialized services in Angular world as well through constants! In total DTW Grand Central is: dependency injection service registry (lifecycle maintenance of all services in one place), redux, react, angularJS, router5. Beauty of this is that you can combineReducers for additions to the Redux store on the fly which also means you can extend your routes with future loading js bundles without having to destroy your current heap and virtual DOM! I don't know if people are doing this sort of thing yet for micro-frontends!`,
    tags: ['React', 'AngularJS', 'Vue', 'Redux', 'Router5', 'Micro-frontends'],
    category: 'professional',
    featured: true,
    startDate: '2018-01-01',
    endDate: '2020-01-01',
    status: 'completed',
    highlights: [
      'Multi-framework integration (React + Vue + Angular)',
      'Dependency injection service registry',
      'Redux with dynamic reducer composition',
      'Built for Amplify Education curriculum platform',
      'Pioneering micro-frontend architecture',
    ],
  },
  {
    id: 'open-maps',
    title: '@dtw/open-maps',
    description: 'Self-hosted map rendering service using OpenStreetMap, MapTiler, Docker, and Redis for worldwide coverage',
    longDescription: `An open source project that powers downthewhole.com by using Open Street Map, Open Map Tiles, Docker and redis to render maps of the whole world! Two main databases: 1 for OSM and 1 for custom locations and then a redis cache for hot-searched geo positions! GEORADIUS command is amazing!`,
    tags: ['OpenStreetMap', 'Docker', 'Redis', 'PostgreSQL', 'Geospatial', 'PostGIS'],
    category: 'open-source',
    featured: true,
    startDate: '2019-01-01',
    status: 'coming-soon',
    highlights: [
      'Render maps of the entire world',
      'Redis GEORADIUS for fast spatial queries',
      'Docker-based deployment',
      'OSM data + custom locations',
    ],
  },
  {
    id: 'ax-ios-app',
    title: '@dtw/ax',
    description: 'iOS personal knowledge management app for offline-first note-taking, travel tips, entertainment reviews, and more',
    longDescription: `An iOS app for personal knowledge: aggregate travel tips (what plug do I need, etc), entertainment/art reviews, ssh client, personal finance, identification storage, link saving, etymology/vocabulary, music making, music discovery, ebook highlights, note taking. For a while now I've been dipping my toes into React Native as a platform and I fully expect to open source my tooling along the way! All of the above are things I've thought of as I've gone about my life navigating the subway to and from everywhere in NYC when I have a hot minute in the tunnel without internet and a thought that springs into my head. We need to start making use of your phones computational power for something other than just games. Think of it like Spotify offline downloads but with all of the easy to reach data that you need throughout your day to day life. When you get back on the network you can sync back up and even exchange notes from your ebook/Kindle with friends!`,
    tags: ['React Native', 'iOS', 'Offline-first', 'Knowledge Management', 'Mobile', 'TypeScript', 'Personal Knowledge', 'NYC'],
    category: 'personal',
    featured: false,
    startDate: '2019-01-01',
    status: 'coming-soon',
    highlights: [
      'Offline-first personal knowledge system',
      'Travel tips, finance, notes, SSH client',
      'Music making and discovery tools',
      'Ebook highlight syncing',
      'Built with React Native',
    ],
  },
  {
    id: 'notes-intelligence',
    title: '@dtw/notes',
    description: 'Intelligent note-taking system with auto-linking, metadata extraction, and AI-powered typeahead for VSCode',
    longDescription: `The intelligence engine driving my note taking. Every time I write a link in my text editor (VSCode) it detects that I just typed a url and silently makes a request to a server that then looks up meta tags, open graph data and even article text using tools like codelucas/newspaper. Markdown is a beautiful "language" and I've been using it since I graduated from high school for all my notes collecting links, thoughts, life events, etc and I want to enable Gmail predictive style typeahead and other things to further my AI knowledge. A while back there was this small Mac app (the name evades me) that allows you to create a decision network graph and other zoom-in-able data structures using bulleted lists and other weird markup that I want to enable. Think of it like an always growing automatic table of contents for everything you write. A corpus but more Wikipedia-esque!`,
    tags: ['VSCode', 'Markdown', 'AI', 'Knowledge Graph', 'NLP', 'TypeScript', 'Open Graph', 'Note Taking'],
    category: 'personal',
    featured: false,
    startDate: '2020-01-01',
    status: 'coming-soon',
    highlights: [
      'Automatic link metadata extraction',
      'AI-powered predictive typeahead',
      'Decision network graphs from markdown',
      'Automatic table of contents generation',
      'VSCode extension',
    ],
  },
  {
    id: 'audio-analysis',
    title: '@dtw/audio',
    description: 'Music analysis platform combining Spotify API, The Echo Nest data, and Last.fm scrobbles for deep listening insights',
    longDescription: `Listen to Spotify daily? A little too much? Did you know you can follow other peoples' discover weekly playlists? What if you could dig in further to your music preferences with a little bit of Spotify API secret sauce and an acquired company called The Echo Nest. A special API from Spotify allows you to get data for each and every song you play. What BPMs do I like in the morning? What key of songs do I like in the evenings in the winter time? Combine your daily Last.FM scrobbles with Spotify api and boom you have a full music analysis tool. This one I'm still trying to figure out what I want to do with it. Houndify should be releasing their Soundhound api soon and we'll all have full Shazam powers and the copyright industry's crazy metrics for detecting if a certain piece of sound already exists. Crazy stuff but I think there's a lot to do creatively here.`,
    tags: ['Python', 'Spotify API', 'Last.fm', 'librosa', 'Music Analysis', 'Data Visualization'],
    category: 'personal',
    featured: true,
    startDate: '2019-01-01',
    status: 'active',
    highlights: [
      'Analyze listening patterns by time/mood',
      'Spotify API + Echo Nest integration',
      'Last.fm scrobble analysis',
      'BPM and key preferences over time',
      'See it in action on /music page',
    ],
    blogPostUrl: '/blog',
    demoUrl: '/music',
  },
  {
    id: 'cdbattaglia-website',
    title: 'cdbattaglia.com',
    description: 'Personal website with music analysis, blog, and portfolio built with TanStack Start, React, and Python',
    tags: ['React', 'TypeScript', 'TanStack Start', 'Python', 'FastAPI', 'PostgreSQL', 'Redis', 'Pulumi'],
    category: 'personal',
    githubUrl: 'https://github.com/cdbattags/cdbattaglia',
    demoUrl: 'https://cdbattaglia.com',
    featured: true,
    startDate: '2024-01-01',
    status: 'active',
    highlights: [
      'Server-side rendered with TanStack Start',
      'Real-time audio analysis with librosa',
      'Type-safe APIs with tRPC',
      'Self-hosted on Digital Ocean',
      'Infrastructure as code with Pulumi',
    ],
  },
];

/**
 * Get all unique tags from projects
 */
export function getAllProjectTags(): string[] {
  const tagSet = new Set<string>();
  projects.forEach(project => {
    project.tags.forEach(tag => tagSet.add(tag));
  });
  return Array.from(tagSet).sort();
}

/**
 * Get projects by category
 */
export function getProjectsByCategory(category: string): Project[] {
  if (category === 'all') return projects;
  return projects.filter(p => p.category === category);
}

/**
 * Get projects by status
 */
export function getProjectsByStatus(status: string): Project[] {
  if (status === 'all') return projects;
  return projects.filter(p => p.status === status);
}

/**
 * Search projects by query
 */
export function searchProjects(query: string): Project[] {
  if (!query) return projects;
  
  const lowerQuery = query.toLowerCase();
  return projects.filter(project =>
    project.title.toLowerCase().includes(lowerQuery) ||
    project.description.toLowerCase().includes(lowerQuery) ||
    project.tags.some(tag => tag.toLowerCase().includes(lowerQuery))
  );
}

/**
 * Filter projects by tags
 */
export function filterProjectsByTags(tags: string[]): Project[] {
  if (tags.length === 0) return projects;
  
  return projects.filter(project =>
    tags.every(tag => project.tags.includes(tag))
  );
}

/**
 * Get featured projects
 */
export function getFeaturedProjects(): Project[] {
  return projects.filter(p => p.featured);
}
